<template>
  <div :class="['participation-chart']">
    <base-chart :options="chartOptions" />
  </div>
</template>

<script>

import BaseChart from '@/components/dashboard/BaseChart.vue';
import colors from 'shared/colors';
import { DASH, DATETIME, DOT, LINE } from '@/lib/constants';
import { DateTime } from 'luxon';
import { formatTooltipCard } from '@/composables/grid-page/chart/utils';

export default {
  name: 'ParticipationChart',
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    minX: {
      type: Number,
      default: null,
    },
    highlightX: {
      type: Number,
      default: null,
    },
  },
  components: { BaseChart },
  emits: ['point-click'],
  computed: {
    data() {
      const data = this.chartData.map((e) => ({
        x: e.x,
        y: e.y,
        participation: e.participation,
        selected: e.x === this.highlightX,
      }));
      data.sort((a, b) => (a.x <= b.x ? -1 : 1));
      return data;
    },
    chartOptions() {
      const emitPointClick = (x) => this.$emit('point-click', x);
      return {
        chart: {
          height: 150,
          marginBottom: 22,
          marginRight: 8,
        },
        series: [
          {
            type: LINE,
            data: this.data,
            clip: false,
            cursor: 'pointer',
            point: {
              events: {
                click() {
                  emitPointClick(this.x);
                },
              },
            },
            marker: {
              enabled: true,
              fillColor: '#FFFFFF',
              lineWidth: 2,
              lineColor: null, // inherit from series
              symbol: 'circle',
              states: {
                hover: { radiusPlus: 2, lineWidthPlus: 0 },
                select: { fillColor: '#FFFFFF', lineColor: this.$colors.grey.darken3, lineWidth: 2, radius: 6 },
              },
            },
            states: {
              hover: { halo: { size: 8 } },
              select: { halo: { size: 8 } },
            },
            connectNulls: true,
            color: this.$colors.grey.darken3,
            dashStyle: DASH,
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
              },
              stops: [[0, 'transparent'], [1, 'transparent']],
            },
          },
        ],
        time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        xAxis: {
          type: DATETIME,
          minTickInterval: 28 * 24 * 3600 * 1000,
          minRange: 28 * 24 * 3600 * 1000,
          min: this.minX,
        },
        yAxis: {
          gridLineDashStyle: DOT,
          title: { text: '' },
          labels: {
            formatter() {
              return `${this.value} %`;
            },
          },
          max: 100,
          min: 0,
          endOnTick: false,
          startOnTick: false,
          tickAmount: 5,
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: this.tooltipFormatter((key) => this.$t(key)),
        },
      };
    },
  },
  methods: {
    tooltipFormatter($t) {
      return function formatter() {
        const point = this.points?.[0]?.point ?? this.point;
        if (!point) {
          return false;
        }
        const tag = `background:${colors.grey.lighten5};border-radius:4px;padding:1px 3px`;
        const row = 'display:flex;align-items:center;gap:.6rem';
        const date = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${DateTime.fromMillis(point.x).toLocaleString(DateTime.DATETIME_MED)}</div>`;
        const participation = `<div style="${row}"><span style="${tag}">${point.y}% (${point.participation})</span><span style="color:${colors.grey.lighten1};margin-left:auto">${$t('participationChart.participation')}</span></div>`;
        return formatTooltipCard(date + participation);
      };
    },
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .goal-progress-chart {
    &.-loading {
      opacity: .5;
    }
  }
</style>
